import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Button, Table } from 'antd';
import { selectFetchTaxRatesRequest } from '../../../../state/ui/configuration/taxes/taxUiReducer';
import { requestAllTaxRates, editTaxRate, requestDeleteTaxRate } from '../../../../state/ui/configuration/taxes/taxUiActions';
import { selectTaxRates } from '../../../../state/domain/taxes/taxRateReducer';
import ConfigHeading from '../../configure/components/ConfigHeading';
import EditTaxRate from './EditTaxRate';
import { DATE_FORMAT } from '../packageUtil';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';


const taxRateShape = PropTypes.shape({
  dateFrom: PropTypes.string.isRequired,
  taxRate: PropTypes.number.isRequired,
});


function TaxRatesView({
  loading, taxRates, onEdit, onDelete,
}) {
  const sortedTaxRates = [...taxRates].sort((a, b) => new Date(b.dateFrom) - new Date(a.dateFrom));
  const currentTaxRate = sortedTaxRates.find(taxRate => new Date() > new Date(taxRate.dateFrom)); // Note: Depends on the list being desc order

  // Component: The button at the top-right for creating a new tax-ratge.
  const CreateButton = () => (
    <Button type="primary" onClick={() => onEdit(null)} loading={loading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // Component: The row total displayed at the bottom of the table.
  function RowTotal() {
    if (!taxRates) return <></>;
    return (
      <div>
        <span>{`${taxRates.length} `}</span>
        <FormattedMessage id="items" />
      </div>
    );
  }

  return (
    <ConfigHeading titleId="taxRate" actions={<CreateButton />}>
      <Table
        pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
        loading={loading}
        dataSource={sortedTaxRates}
        rowKey={tax => tax.dateFrom}
        rowClassName={(record) => {
          if (record === currentTaxRate) return 'row-active';
          if (new Date() < new Date(record.dateFrom)) return 'row-upcoming';
          return 'row-inactive';
        }}
      >
        <Table.Column
          defaultSortOrder="descend"
          title={<FormattedMessage id="startDate" />}
          dataIndex="dateFrom"
          key="dateFrom"
          render={text => (
            <span title={text}>{moment(text).format(DATE_FORMAT)}</span>
          )}
        />
        <Table.Column
          title={<FormattedMessage id="taxRates" />}
          align="right"
          dataIndex="taxRate"
          key="taxRate"
          render={rate => `${Number(rate * 100).toFixed(2)}%`}
        />
        <Table.Column
          className="via-action-column"
          align="center"
          render={(text, record) => {
            const isFutureDated = new Date(record.dateFrom) > new Date();
            return (
              <div className="button-flex-col">
                { isFutureDated && (
                  <ConfirmButton size="small" type="danger" onClick={() => onDelete(record)} icon="stop">
                    <FormattedMessage id="remove" />
                  </ConfirmButton>
                )}
                {isFutureDated && (
                  <Button size="small" type="primary" onClick={() => onEdit(record)} icon="edit">
                    <FormattedMessage id="edit" />
                  </Button>
                )}
              </div>
            );
          }}
        />
      </Table>
      <EditTaxRate />
    </ConfigHeading>
  );
}

TaxRatesView.propTypes = {
  loading: PropTypes.bool,
  taxRates: PropTypes.arrayOf(taxRateShape),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

TaxRatesView.defaultProps = {
  loading: false,
  taxRates: [],
};


export default function TaxRates() {
  // Redux Actions
  const dispatch = useDispatch();
  const editTax = taxRate => dispatch(editTaxRate(taxRate && taxRate.dateFrom));
  const deleteTax = taxRate => dispatch(requestDeleteTaxRate(taxRate));

  // Redux State
  const taxRatesRequest = useSelector(selectFetchTaxRatesRequest);
  const isLoading = !!(taxRatesRequest && taxRatesRequest.isLoading);
  const taxRates = useSelector(selectTaxRates);

  // Load tax rates on first entry.
  useEffect(() => {
    dispatch(requestAllTaxRates());
  }, [dispatch]);

  return <TaxRatesView loading={isLoading} taxRates={taxRates} onEdit={editTax} onDelete={deleteTax} />;
}
